import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import config from '../config';
import PawnPhotoThumbs from './pawn/PawnPhotoThumbs';

const API = config.API_URL;

function inr(n) {
  const v = Number(n);
  if (!v && v !== 0) return '—';
  return `₹${Math.round(v).toLocaleString('en-IN')}`;
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function toDateInputValue(d = new Date()) {
  const x = new Date(d);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const EMPTY_FORM = {
  customerPhone: '',
  customerName: '',
  customerAddress: '',
  productName: '',
  imei: '',
  deviceLock: '',
  principalAmount: '',
  interestRatePercent: '10',
  lateFeePerDay: '20',
  pledgedAt: toDateInputValue(),
  dueDate: '',
};

function PawnDashboard({ mobileApp = false, onStatsChange, refreshTick = 0 }) {
  const [stats, setStats] = useState(null);
  const [listFilter, setListFilter] = useState('active');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(mobileApp ? 'home' : 'new');
  const [mobileTab, setMobileTab] = useState('home');
  const [form, setForm] = useState(EMPTY_FORM);
  const [lookup, setLookup] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [redeemTarget, setRedeemTarget] = useState(null);
  const [collectAmount, setCollectAmount] = useState('');
  const [collectNote, setCollectNote] = useState('');
  const [soldAmount, setSoldAmount] = useState('');
  const [soldDate, setSoldDate] = useState(toDateInputValue());
  const [soldNote, setSoldNote] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [selling, setSelling] = useState(false);
  const [search, setSearch] = useState('');
  const [photos, setPhotos] = useState([]);
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const lookupSkipRef = useRef(0);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4500);
  };

  const loadStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pawn/stats`);
      setStats(res.data);
      onStatsChange?.();
    } catch (e) {
      console.error(e);
    }
  }, [onStatsChange]);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (listFilter === 'active') params.set('status', 'active');
      else if (listFilter === 'today') params.set('due', 'today');
      else if (listFilter === 'overdue') params.set('due', 'overdue');
      else if (listFilter === 'redeemed') params.set('status', 'redeemed');
      else if (listFilter === 'sold') params.set('status', 'sold');
      else if (listFilter === 'sellable') params.set('due', 'sellable');
      else if (listFilter === 'all') params.set('status', 'all');
      if (search.trim()) params.set('q', search.trim());
      const res = await axios.get(`${API}/pawn?${params}`);
      setTickets(res.data || []);
    } catch (e) {
      showToast(e?.response?.data?.message || 'Could not load list', 'danger');
    } finally {
      setLoading(false);
    }
  }, [listFilter, search]);

  useEffect(() => {
    loadStats();
  }, [loadStats, refreshTick]);

  useEffect(() => {
    loadTickets();
  }, [loadTickets, refreshTick]);

  const runPreview = useCallback(async () => {
    const principal = Number(form.principalAmount);
    if (!principal || !form.dueDate) {
      setPreview(null);
      return;
    }
    try {
      const res = await axios.post(`${API}/pawn/preview`, {
        principalAmount: principal,
        interestRatePercent: Number(form.interestRatePercent) || 10,
        lateFeePerDay: Number(form.lateFeePerDay) || 20,
        pledgedAt: form.pledgedAt,
        dueDate: form.dueDate,
      });
      setPreview(res.data.preview);
    } catch {
      setPreview(null);
    }
  }, [form]);

  useEffect(() => {
    runPreview();
  }, [runPreview]);

  useEffect(() => {
    const digits = String(form.customerPhone || '').replace(/\D/g, '').slice(-10);
    if (digits.length !== 10) {
      setLookup(null);
      return undefined;
    }
    const seq = ++lookupSkipRef.current;
    const timer = setTimeout(async () => {
      setLookupLoading(true);
      try {
        const res = await axios.get(`${API}/pawn/lookup/phone/${digits}`);
        if (seq !== lookupSkipRef.current) return;
        setLookup(res.data);
        if (res.data.found) {
          setForm((f) => ({
            ...f,
            customerPhone: digits,
            customerName: res.data.customer.name || f.customerName,
            customerAddress: res.data.customer.address || f.customerAddress,
          }));
        } else {
          setForm((f) => ({ ...f, customerPhone: digits }));
        }
      } catch {
        if (seq === lookupSkipRef.current) setLookup(null);
      } finally {
        if (seq === lookupSkipRef.current) setLookupLoading(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [form.customerPhone]);

  const onPhotoPick = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4);
    setPhotos(files);
    photoPreviews.forEach((u) => URL.revokeObjectURL(u));
    setPhotoPreviews(files.map((f) => URL.createObjectURL(f)));
  };

  useEffect(() => {
    return () => {
      photoPreviews.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [photoPreviews]);

  const submitPledge = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('customerPhone', String(form.customerPhone).replace(/\D/g, '').slice(-10));
      fd.append('customerName', form.customerName);
      if (form.customerAddress) fd.append('customerAddress', form.customerAddress);
      fd.append('productName', form.productName);
      if (form.imei) fd.append('imei', form.imei);
      if (form.deviceLock) fd.append('deviceLock', form.deviceLock);
      fd.append('pledgedAt', form.pledgedAt);
      fd.append('dueDate', form.dueDate);
      fd.set('principalAmount', String(Number(form.principalAmount)));
      fd.set('interestRatePercent', String(Number(form.interestRatePercent) || 10));
      fd.set('lateFeePerDay', String(Number(form.lateFeePerDay) || 20));
      photos.forEach((file) => fd.append('photos', file));

      const res = await axios.post(`${API}/pawn`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showToast(`Saved ${res.data.ticket.ticketNumber} — pay customer ${inr(res.data.ticket.disbursementAmount)}`);
      setForm({ ...EMPTY_FORM, pledgedAt: toDateInputValue() });
      setPhotos([]);
      photoPreviews.forEach((u) => URL.revokeObjectURL(u));
      setPhotoPreviews([]);
      setLookup(null);
      setPreview(null);
      if (mobileApp) {
        setMobileTab('list');
        setListFilter('active');
      } else {
        setTab('list');
      }
      loadStats();
      loadTickets();
    } catch (e) {
      showToast(e?.response?.data?.message || 'Save failed', 'danger');
    } finally {
      setSubmitting(false);
    }
  };

  const openRedeem = (ticket) => {
    setRedeemTarget(ticket);
    setCollectAmount(String(ticket.redemption?.totalDue ?? ticket.principalAmount ?? ''));
    setCollectNote('');
    setSoldAmount(String(ticket.disbursementAmount || ''));
    setSoldDate(toDateInputValue());
    setSoldNote('');
  };

  const confirmRedeem = async () => {
    if (!redeemTarget) return;
    const paid = Number(collectAmount);
    if (!paid || paid < 1) {
      showToast('Enter amount collected', 'warning');
      return;
    }
    setRedeeming(true);
    try {
      await axios.post(`${API}/pawn/${redeemTarget._id}/redeem`, {
        amountPaid: paid,
        note: collectNote,
      });
      showToast(`Collected ${inr(paid)}`);
      setRedeemTarget(null);
      loadStats();
      loadTickets();
    } catch (e) {
      showToast(e?.response?.data?.message || 'Redeem failed', 'danger');
    } finally {
      setRedeeming(false);
    }
  };

  const confirmSold = async () => {
    if (!redeemTarget) return;
    const amt = Number(soldAmount);
    if (!amt || amt < 1) {
      showToast('Enter sold amount', 'warning');
      return;
    }
    setSelling(true);
    try {
      await axios.post(`${API}/pawn/${redeemTarget._id}/sell`, {
        soldAmount: amt,
        soldAt: soldDate,
        note: soldNote,
      });
      showToast(`Marked sold ${inr(amt)} on ${soldDate}`);
      setRedeemTarget(null);
      loadStats();
      loadTickets();
    } catch (e) {
      showToast(e?.response?.data?.message || 'Could not mark sold', 'danger');
    } finally {
      setSelling(false);
    }
  };

  const activeFromLookup = useMemo(() => lookup?.activeTickets || [], [lookup]);

  const pledgeForm = (
    <form onSubmit={submitPledge} className={`pawn-form-stack ${mobileApp ? '' : 'card shadow-sm'}`}>
      <div className={mobileApp ? '' : 'card-body'}>
        {!mobileApp && <h5 className="card-title">New pledge</h5>}

        <div className="mb-3">
          <label className="form-label">Mobile number</label>
          <input
            className="form-control"
            inputMode="numeric"
            autoComplete="tel"
            value={form.customerPhone}
            onChange={(e) => setForm({ ...form, customerPhone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
            placeholder="10 digits"
            maxLength={10}
            required
          />
          {lookupLoading && <small className="text-muted">Looking up…</small>}
          {lookup?.found && !lookupLoading && (
            <small className="text-success d-block">
              Existing customer · purchases {lookup.customer.purchaseCount} · repairs {lookup.customer.repairCount}
              {!mobileApp && (
                <Link className="ms-1" to={`/customer/${lookup.customer._id}`}>Profile</Link>
              )}
            </small>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label">Customer name</label>
          <input
            className="form-control"
            value={form.customerName}
            onChange={(e) => setForm({ ...form, customerName: e.target.value })}
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Address <span className="text-muted fw-normal">(optional)</span></label>
          <input
            className="form-control"
            value={form.customerAddress}
            onChange={(e) => setForm({ ...form, customerAddress: e.target.value })}
          />
        </div>

        {activeFromLookup.length > 0 && (
          <div className="alert alert-warning py-2 small">
            Active pledge:{' '}
            {activeFromLookup.map((t) => (
              <span key={t._id} className="d-block">
                {t.ticketNumber} — {inr(t.principalAmount)} until {formatDate(t.dueDate)}
              </span>
            ))}
          </div>
        )}

        <div className="mb-3">
          <label className="form-label">Product name</label>
          <input
            className="form-control"
            value={form.productName}
            onChange={(e) => setForm({ ...form, productName: e.target.value })}
            placeholder="e.g. iQOO Z9 5G"
            required
          />
        </div>

        <div className="mb-3">
          <label className="form-label">IMEI <span className="text-muted fw-normal">(optional)</span></label>
          <input
            className="form-control"
            value={form.imei}
            onChange={(e) => setForm({ ...form, imei: e.target.value })}
            inputMode="numeric"
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Screen lock <span className="text-muted fw-normal">(PIN / pattern)</span></label>
          <input
            className="form-control"
            value={form.deviceLock}
            onChange={(e) => setForm({ ...form, deviceLock: e.target.value })}
            placeholder="e.g. 2580 or pattern note"
            autoComplete="off"
          />
        </div>

        <div className="mb-3">
          <label className="form-label">Pledge photo <span className="text-muted fw-normal">(optional, max 4)</span></label>
          <input
            type="file"
            className="form-control"
            accept="image/*"
            capture="environment"
            multiple
            onChange={onPhotoPick}
          />
          {photoPreviews.length > 0 && (
            <div className="d-flex flex-wrap gap-2 mt-2">
              {photoPreviews.map((src, i) => (
                <img
                  key={src}
                  src={src}
                  alt={photos[i]?.name || 'Preview'}
                  style={{ width: 88, height: 88, objectFit: 'cover', borderRadius: 10, border: '2px solid #fcd34d' }}
                />
              ))}
            </div>
          )}
        </div>

        <div className="row g-2 mb-3">
          <div className="col-6">
            <label className="form-label">Redeem amount</label>
            <input
              type="number"
              className="form-control"
              value={form.principalAmount}
              onChange={(e) => setForm({ ...form, principalAmount: e.target.value })}
              required
              min={1}
              inputMode="numeric"
            />
          </div>
          <div className="col-6">
            <label className="form-label">Interest %</label>
            <input
              type="number"
              className="form-control"
              value={form.interestRatePercent}
              onChange={(e) => setForm({ ...form, interestRatePercent: e.target.value })}
              min={0}
              max={100}
              step="0.5"
            />
          </div>
          <div className="col-6">
            <label className="form-label">Late fee / day</label>
            <input
              type="number"
              className="form-control"
              value={form.lateFeePerDay}
              onChange={(e) => setForm({ ...form, lateFeePerDay: e.target.value })}
              min={0}
            />
          </div>
          <div className="col-6">
            <label className="form-label">Pledge date</label>
            <input
              type="date"
              className="form-control"
              value={form.pledgedAt}
              onChange={(e) => setForm({ ...form, pledgedAt: e.target.value })}
            />
          </div>
          <div className="col-12">
            <label className="form-label">Pickup date</label>
            <input
              type="date"
              className="form-control"
              value={form.dueDate}
              onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
              required
            />
          </div>
        </div>

        {preview && (
          <div className="pawn-preview-box">
            <div className="small text-muted mb-1">{preview.tenureDays} days · interest {inr(preview.interestAmount)}</div>
            <div className="small">Pay customer now</div>
            <div className="cash-out">{inr(preview.disbursementAmount)}</div>
            <div className="small text-muted mt-1">
              On-time pickup: {inr(preview.principalAmount)} · Late: {inr(preview.lateFeePerDay)}/day after pickup date
            </div>
          </div>
        )}

        <button type="submit" className={`btn btn-warning text-dark fw-bold w-100 ${mobileApp ? 'btn-lg' : ''}`} disabled={submitting || !preview}>
          {submitting ? 'Saving…' : 'Save pledge'}
        </button>
      </div>
    </form>
  );

  const filterChips = [
    { key: 'active', label: 'Active' },
    { key: 'today', label: 'Due today' },
    { key: 'overdue', label: 'Overdue' },
    { key: 'sellable', label: '60+ days' },
    { key: 'redeemed', label: 'Redeemed' },
    { key: 'sold', label: 'Sold' },
  ];

  const ticketCards = (
    <>
      <div className="d-flex flex-wrap gap-2 mb-2">
        {filterChips.map((f) => (
          <button
            key={f.key}
            type="button"
            className={`btn btn-sm ${listFilter === f.key ? 'btn-warning text-dark' : 'btn-outline-secondary'}`}
            onClick={() => setListFilter(f.key)}
          >
            {f.label}
          </button>
        ))}
      </div>
      <input
        className="form-control mb-3"
        placeholder="Search ticket, phone, IMEI…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {loading ? (
        <div className="text-center py-4"><div className="spinner-border text-warning" /></div>
      ) : tickets.length === 0 ? (
        <p className="text-muted text-center py-4">No tickets</p>
      ) : (
        tickets.map((t) => {
          const due = t.redemption;
          const isLate = due && !due.onTime;
          return (
            <div key={t._id} className={`pawn-ticket-card ${isLate ? 'overdue' : ''}`}>
              {(t.intakePhotos || []).length > 0 && (
                <div className="mb-2">
                  <PawnPhotoThumbs urls={t.intakePhotos} size={mobileApp ? 96 : 72} />
                </div>
              )}
              <div className="d-flex justify-content-between align-items-start gap-2">
                <div>
                  <code className="small">{t.ticketNumber}</code>
                  <div className="fw-semibold">{t.customerName}</div>
                  <div className="small text-muted">{t.customerPhone}</div>
                  <div className="small mt-1">{t.deviceLabel || t.deviceModel}</div>
                  {t.deviceLock && (
                    <div className="small mt-1"><i className="bi bi-lock me-1" />Lock: <strong>{t.deviceLock}</strong></div>
                  )}
                </div>
                <div className="text-end">
                  <div className="small text-muted">Pickup {formatDate(t.dueDate)}</div>
                  {t.status === 'active' && due ? (
                    <div className={`fw-bold ${isLate ? 'text-danger' : ''}`}>{inr(due.totalDue)}</div>
                  ) : (
                    <span className="badge bg-secondary">{t.status}</span>
                  )}
                </div>
              </div>
              {t.status === 'active' && (
                <button type="button" className="btn btn-success w-100 mt-2" onClick={() => openRedeem(t)}>
                  Pickup & redeem
                </button>
              )}
              {t.status === 'sold' && (
                <div className="small text-danger mt-2">
                  Sold {formatDate(t.soldAt)} · {inr(t.soldAmount)}
                </div>
              )}
              {t.status === 'redeemed' && (
                <div className="small text-success mt-2">
                  Collected {inr(t.redemptionAmountPaid)} on {formatDate(t.redeemedAt)}
                </div>
              )}
            </div>
          );
        })
      )}
    </>
  );

  const statsGrid = stats && (
    <div className="repair-stat-grid">
      <div className="repair-stat-card accent-orange">
        <div className="stat-icon"><i className="bi bi-box-seam" /></div>
        <div className="stat-value">{stats.activeCount}</div>
        <div className="stat-label">Active</div>
      </div>
      <div className="repair-stat-card accent-green">
        <div className="stat-icon"><i className="bi bi-calendar-check" /></div>
        <div className="stat-value">{stats.dueToday}</div>
        <div className="stat-label">Due today</div>
      </div>
      <div className="repair-stat-card accent-blue">
        <div className="stat-icon"><i className="bi bi-exclamation-triangle" /></div>
        <div className="stat-value">{stats.overdue}</div>
        <div className="stat-label">Overdue</div>
      </div>
      <div className="repair-stat-card accent-purple">
        <div className="stat-icon"><i className="bi bi-cash" /></div>
        <div className="stat-value" style={{ fontSize: '1.1rem' }}>{inr(stats.cashDisbursedOutstanding)}</div>
        <div className="stat-label">Cash out</div>
      </div>
    </div>
  );

  const mobileHome = (
    <div className="pawn-mobile-pad">
      <div className="repair-home-greeting">Pawn counter</div>
      <div className="repair-home-sub">Main branch · quick pledge & pickup</div>
      {statsGrid}
      <div className="repair-quick-actions">
        <button type="button" className="repair-quick-btn btn-warning text-dark" onClick={() => setMobileTab('new')}>
          <i className="bi bi-plus-circle" />
          New pledge
        </button>
        <button type="button" className="repair-quick-btn btn-outline-secondary" onClick={() => { setMobileTab('list'); setListFilter('overdue'); }}>
          <i className="bi bi-alarm" />
          Overdue
        </button>
      </div>
      {stats?.dueToday > 0 && (
        <button type="button" className="btn btn-outline-warning w-100 mb-2" onClick={() => { setMobileTab('list'); setListFilter('today'); }}>
          {stats.dueToday} pickup due today
        </button>
      )}
      {stats?.sellableCount > 0 && (
        <button type="button" className="btn btn-outline-danger w-100 mb-2" onClick={() => { setMobileTab('list'); setListFilter('sellable'); }}>
          {stats.sellableCount} eligible to sell (60+ days)
        </button>
      )}
    </div>
  );

  const desktopHeader = !mobileApp && (
    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
      <div>
        <h2 className="h4 mb-0"><i className="bi bi-gem me-2" />Pawn</h2>
        <small className="text-muted">Mobile app: <Link to="/pawn-app">Add shortcut</Link></small>
      </div>
      <div className="d-flex gap-2">
        <Link className="btn btn-outline-warning btn-sm" to="/pawn-app"><i className="bi bi-phone me-1" />Open app</Link>
        <Link className="btn btn-outline-secondary btn-sm" to="/pawn-analysis"><i className="bi bi-graph-up me-1" />Analysis</Link>
      </div>
    </div>
  );

  return (
    <div className={mobileApp ? 'pawn-mobile-pad' : 'pawn-dashboard'}>
      {toast && (
        <div
          className={`alert alert-${toast.type} alert-dismissible fade show shadow ${mobileApp ? 'pawn-toast position-fixed' : ''}`}
          style={mobileApp ? { zIndex: 2000 } : undefined}
          role="alert"
        >
          {toast.msg}
          <button type="button" className="btn-close" onClick={() => setToast(null)} />
        </div>
      )}

      {desktopHeader}

      {!mobileApp && statsGrid && <div className="mb-3">{statsGrid}</div>}

      {!mobileApp && (
        <ul className="nav nav-tabs mb-3">
          <li className="nav-item">
            <button type="button" className={`nav-link ${tab === 'new' ? 'active' : ''}`} onClick={() => setTab('new')}>New pledge</button>
          </li>
          <li className="nav-item">
            <button type="button" className={`nav-link ${tab === 'list' ? 'active' : ''}`} onClick={() => setTab('list')}>Pickup list</button>
          </li>
        </ul>
      )}

      {mobileApp && mobileTab === 'home' && mobileHome}
      {mobileApp && mobileTab === 'new' && pledgeForm}
      {mobileApp && mobileTab === 'list' && ticketCards}

      {!mobileApp && tab === 'new' && pledgeForm}
      {!mobileApp && tab === 'list' && (
        <div className="card shadow-sm">
          <div className="card-body">{ticketCards}</div>
        </div>
      )}

      {mobileApp && (
        <nav className="pawn-app-bottomnav" aria-label="Pawn counter">
          <button type="button" className={mobileTab === 'home' ? 'active' : ''} onClick={() => setMobileTab('home')}>
            <i className="bi bi-house" />
            Home
          </button>
          <button type="button" className={`nav-fab ${mobileTab === 'new' ? 'active' : ''}`} onClick={() => setMobileTab('new')} aria-label="New pledge">
            <i className="bi bi-plus-lg" />
          </button>
          <button type="button" className={mobileTab === 'list' ? 'active' : ''} onClick={() => setMobileTab('list')}>
            <i className="bi bi-list-check" />
            Pickup
          </button>
        </nav>
      )}

      {redeemTarget && (
        <div className="modal show d-block" style={{ background: 'rgba(0,0,0,0.5)', zIndex: mobileApp ? 2100 : undefined }} tabIndex={-1}>
          <div className={`modal-dialog ${mobileApp ? 'modal-fullscreen-sm-down' : ''}`}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Pickup — {redeemTarget.ticketNumber}</h5>
                <button type="button" className="btn-close" onClick={() => setRedeemTarget(null)} />
              </div>
              <div className="modal-body">
                <p><strong>{redeemTarget.customerName}</strong></p>
                <p className="text-muted">{redeemTarget.deviceLabel || redeemTarget.deviceModel}</p>
                <PawnPhotoThumbs urls={redeemTarget.intakePhotos} size={96} />
                {redeemTarget.deviceLock && (
                  <p className="small bg-light rounded p-2"><i className="bi bi-lock me-1" />Screen lock: <strong>{redeemTarget.deviceLock}</strong></p>
                )}
                <div className="small text-muted">System due</div>
                <div className="fs-4 fw-bold">{inr(redeemTarget.redemption?.totalDue)}</div>
                {redeemTarget.redemption?.lateDays > 0 && (
                  <p className="small text-danger mb-2">
                    {redeemTarget.redemption.lateDays} days late × {inr(redeemTarget.lateFeePerDay)}
                    {' '}({inr(redeemTarget.redemption.principalAmount)} + {inr(redeemTarget.redemption.lateFeeAmount)} late)
                  </p>
                )}
                <label className="form-label mt-2">Amount collected (editable)</label>
                <input
                  type="number"
                  className="form-control form-control-lg"
                  min={1}
                  inputMode="numeric"
                  value={collectAmount}
                  onChange={(e) => setCollectAmount(e.target.value)}
                />
                {Number(collectAmount) > 0 && Number(collectAmount) !== Number(redeemTarget.redemption?.totalDue) && (
                  <div className="small mt-1 text-warning">
                    Collecting {inr(collectAmount)} vs due {inr(redeemTarget.redemption?.totalDue)}
                  </div>
                )}
                <label className="form-label mt-2">Note <span className="text-muted fw-normal">(optional)</span></label>
                <input
                  className="form-control"
                  value={collectNote}
                  onChange={(e) => setCollectNote(e.target.value)}
                  placeholder="e.g. waived late fee, cash received"
                />
                {redeemTarget.redemption?.canSell && (
                  <div className="border rounded p-3 mt-3 bg-light">
                    <div className="fw-semibold mb-1">Recovery — mark sold</div>
                    <p className="small text-muted mb-2">
                      Pickup date crossed {redeemTarget.redemption.sellAfterDays}+ days ({redeemTarget.redemption.lateDays} days late).
                      Sold date is saved for records.
                    </p>
                    <label className="form-label">Sold amount</label>
                    <input
                      type="number"
                      className="form-control mb-2"
                      min={1}
                      inputMode="numeric"
                      value={soldAmount}
                      onChange={(e) => setSoldAmount(e.target.value)}
                    />
                    <label className="form-label">Sold date</label>
                    <input
                      type="date"
                      className="form-control mb-2"
                      value={soldDate}
                      onChange={(e) => setSoldDate(e.target.value)}
                    />
                    <label className="form-label">Sale note</label>
                    <input
                      className="form-control"
                      value={soldNote}
                      onChange={(e) => setSoldNote(e.target.value)}
                      placeholder="Buyer / shop sale note"
                    />
                    <button type="button" className="btn btn-outline-danger w-100 mt-2" disabled={selling || redeeming} onClick={confirmSold}>
                      {selling ? '…' : 'Mark as sold'}
                    </button>
                  </div>
                )}
              </div>
              <div className="modal-footer flex-column gap-2">
                <button type="button" className="btn btn-success btn-lg w-100" disabled={redeeming || selling} onClick={confirmRedeem}>
                  {redeeming ? '…' : 'Collect & return phone'}
                </button>
                <button type="button" className="btn btn-secondary w-100" onClick={() => setRedeemTarget(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PawnDashboard;
